import { z } from 'zod';
import type React from 'react';
import { str } from '../../../platform/api/utils';

// Apoio stateless do ObligationDetail (régua de 400 linhas): rótulos, schema,
// esqueleto e estilos — extraídos sem mudança de comportamento.
export const TYPE_LABELS: Record<string, string> = {
  PERSON_OBLIGATION: 'Obrigação pessoal',
  HOUSE_OBLIGATION: 'Obrigação da casa',
  TIME_CYCLE: 'Ciclo temporal',
  ANNUAL_OBLIGATION: 'Obrigação anual',
  ROLE_OBLIGATION: 'Obrigação de cargo',
  INITIATION_RELATED: 'Relacionada a iniciação',
  ANCESTOR_RELATED: 'Relacionada a ancestral',
  CALENDAR_RELATED: 'Relacionada ao calendário',
  EMERGENCY: 'Emergência',
  OTHER: 'Outro',
};

export const STATUS_LABELS: Record<string, string> = {
  PLANNED: 'Planejada',
  IN_PREPARATION: 'Em preparação',
  PENDING_DECISION: 'Aguardando decisão',
  PENDING_MATERIALS: 'Aguardando materiais',
  PENDING_FINANCE: 'Aguardando finanças',
  PENDING_PEOPLE: 'Aguardando pessoas',
  SCHEDULED: 'Agendada',
  COMPLETED: 'Concluída',
  POSTPONED: 'Adiada',
  CANCELLED: 'Cancelada',
  ARCHIVED: 'Arquivada',
  RESTRICTED_ARCHIVED: 'Arquivada restrita',
};

export const STATUS_VARIANTS: Record<string, string> = {
  PLANNED: 'default',
  IN_PREPARATION: 'info',
  PENDING_DECISION: 'warning',
  PENDING_MATERIALS: 'warning',
  PENDING_FINANCE: 'warning',
  PENDING_PEOPLE: 'warning',
  SCHEDULED: 'info',
  COMPLETED: 'success',
  POSTPONED: 'danger',
  CANCELLED: 'danger',
  ARCHIVED: 'default',
  RESTRICTED_ARCHIVED: 'danger',
};

export const VISIBILITY_LABELS: Record<string, string> = {
  PUBLIC: 'Público',
  INTERNAL: 'Interno',
  RESTRICTED: 'Restrito',
};

export const VISIBILITY_VARIANTS: Record<string, string> = {
  PUBLIC: 'success',
  INTERNAL: 'info',
  RESTRICTED: 'danger',
};

export const STATUS_REQUIRES_REASON: Record<string, boolean> = {
  CANCELLED: true,
  POSTPONED: true,
  ARCHIVED: true,
  RESTRICTED_ARCHIVED: true,
};

export const editFormSchema = z.object({
  title: z.string().min(1, 'Título é obrigatório').max(255),
  type: z.string().min(1, 'Tipo é obrigatório'),
  visibility: z.string(),
  description: z.string().optional().or(z.literal('')),
  expectedDate: z.string().optional().or(z.literal('')),
  completedDate: z.string().optional().or(z.literal('')),
  restrictedNotes: z.string().optional().or(z.literal('')),
  eventId: z.string().optional().or(z.literal('')),
  visibleInMemberApp: z.boolean().optional(),
  safeDisplayTitle: z.string().optional().or(z.literal('')),
  safeDisplaySummary: z.string().optional().or(z.literal('')),
});

export type EditForm = z.infer<typeof editFormSchema>;

export type Tab = 'dados' | 'responsaveis';

export function displayName(p: Record<string, unknown>): string {
  return str(p.socialName) || str(p.civilName) || str(p.name) || '—';
}

export function fmtDate(v: unknown): string {
  if (typeof v === 'string' && v.length >= 10) return v.substring(0, 10);
  if (v instanceof Date) return v.toISOString().substring(0, 10);
  return '—';
}

export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem' }}>
      <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{label}</span>
      {children}
      {error && <span style={{ color: 'var(--danger)', fontSize: '0.7rem' }}>{error}</span>}
    </label>
  );
}

export function SkeletonDetail() {
  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
      <div
        style={{
          width: 160,
          height: 14,
          background: 'var(--line-soft)',
          borderRadius: 8,
          marginBottom: '1.5rem',
        }}
      />
      <div
        style={{
          width: '60%',
          height: 24,
          background: 'var(--line-soft)',
          borderRadius: 8,
          marginBottom: '2rem',
        }}
      />
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid var(--line)',
            padding: '1.25rem 1.5rem',
            height: 100,
            marginBottom: '1rem',
          }}
        >
          <div
            style={{
              width: '30%',
              height: 14,
              background: 'var(--line-soft)',
              borderRadius: 8,
              marginBottom: '0.75rem',
            }}
          />
          <div
            style={{
              width: '60%',
              height: 12,
              background: 'var(--soft)',
              borderRadius: 8,
              marginBottom: '0.5rem',
            }}
          />
          <div style={{ width: '40%', height: 12, background: 'var(--soft)', borderRadius: 8 }} />
        </div>
      ))}
    </div>
  );
}

export const dlGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '160px 1fr',
  gap: '0.5rem 1rem',
  fontSize: '0.875rem',
};

export const Dt = ({ children }: { children: React.ReactNode }) => (
  <dt style={{ color: 'var(--ink)', fontWeight: 600, margin: 0 }}>{children}</dt>
);

export const Dd = (props: React.HTMLAttributes<HTMLElement> & { children: React.ReactNode }) => (
  <dd style={{ margin: 0, color: 'var(--ink)', ...props.style }}>{props.children}</dd>
);

export const inputCss: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.6rem',
  border: '1px solid var(--line)',
  borderRadius: 10,
  fontSize: '0.85rem',
  boxSizing: 'border-box',
  outline: 'none',
};

export const primaryBtn: React.CSSProperties = {
  background: 'var(--green)',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '0.45rem 1rem',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
};

export const secondaryBtn: React.CSSProperties = {
  background: '#fff',
  color: 'var(--muted)',
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '0.45rem 1rem',
  fontSize: '0.8rem',
  cursor: 'pointer',
};

export const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--muted)',
  cursor: 'pointer',
  fontSize: '0.875rem',
  marginBottom: '0.5rem',
  display: 'block',
  padding: 0,
};

export const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--overlay)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
};
